import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { getCuisineEmoji } from "@/utils/cuisineEmojiMap";
import {
  ArrowLeft, Heart, Share2, MapPin, Clock, Calendar, Shield, Download,
  Users, Star, Flag, ExternalLink, Phone, CreditCard, Banknote, Wifi, Utensils, Eye,
  ShoppingCart,
} from "lucide-react";
import SocialLinksSection from "@/components/shared/SocialLinksSection";
import AccountabilityLog from "@/components/trucks/AccountabilityLog";
import SpotSubmissionSheet from "@/components/trucks/SpotSubmissionSheet";
import WaitTimeDisplay from "@/components/shared/WaitTimeDisplay";
import WaitTimeSheet from "@/components/shared/WaitTimeSheet";
import PunchCard from "@/components/trucks/PunchCard";
import GroupOrder from "@/components/trucks/GroupOrder";
import ExternalLinkButtons from "@/components/shared/ExternalLinkButtons";
import { useAuthGate } from "@/hooks/useAuthGate";
import AuthGateSheet from "@/components/shared/AuthGateSheet";
import TipOfTheDay from "@/components/shared/TipOfTheDay";
import { useSSEWithFallback } from "@/hooks/useSSEWithFallback";

const PAYMENT_ICONS = {
  cash: { icon: Banknote, label: "Cash" },
  card: { icon: CreditCard, label: "Card" },
  contactless: { icon: Wifi, label: "Contactless" },
};

export default function FoodTruckDetail() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { requireAuth, gateProps } = useAuthGate();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFav, setIsFav] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [tracker, setTracker] = useState(null);
  const [showSpot, setShowSpot] = useState(false);
  const [showWaitSheet, setShowWaitSheet] = useState(false);
  const [communityFavs, setCommunityFavs] = useState([]);
  const [smartMenu, setSmartMenu] = useState(null);
  const [weather, setWeather] = useState(null);
  const [weatherPolicy, setWeatherPolicy] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState({});

  const fetchDetail = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/offers/${businessId}`);
      setBiz(res.data);
    } catch {
      toast.error("Could not load truck details");
    } finally { setLoading(false); }
  }, [businessId]);

  const fetchLive = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/offers/${businessId}/truck-live-status`);
      setLiveData(res.data);
    } catch { /* ignore */ }
  }, [businessId]);

  const fetchExtras = useCallback(async () => {
    try {
      const [favRes, verRes, cfRes] = await Promise.all([
        axios.get(`${API}/favorites`),
        axios.get(`${API}/offers/${businessId}/verifications`),
        axios.get(`${API}/businesses/${businessId}/community-favorites`).catch(() => ({ data: { favorites: [] } })),
      ]);
      const favIds = (favRes.data || []).map(f => f.business_id);
      setIsFav(favIds.includes(businessId));
      setCommunityFavs(cfRes.data?.favorites || []);
      setVerifications(verRes.data || []);
    } catch { /* ignore */ }

    try {
      const tRes = await axios.get(`${API}/tracker/${businessId}`);
      setTracker(tRes.data);
    } catch { /* ignore */ }

    try {
      const menuRes = await axios.get(`${API}/menu/${businessId}`);
      if (menuRes.data?.has_menu) setSmartMenu(menuRes.data);
    } catch { /* ignore */ }
  }, [businessId]);

  useEffect(() => {
    fetchDetail();
    fetchExtras();
    // Fetch weather
    axios.get(`${API}/weather/business/${businessId}`).then(res => {
      if (res.data?.weather) setWeather(res.data.weather);
      if (res.data?.weather_policy) setWeatherPolicy(res.data.weather_policy);
    }).catch(() => {});
    // Fetch badges and availability
    axios.get(`${API}/vendor/badges/${businessId}`).then(res => {
      setAllBadges(res.data?.badges || []);
    }).catch(() => {});
    axios.get(`${API}/vendor/availability/${businessId}`).then(res => {
      const dates = res.data?.unavailable_dates || [];
      const reasons = res.data?.unavailable_reason || {};
      const map = {};
      dates.forEach(d => { map[d] = reasons[d] || true; });
      setUnavailableDates(map);
    }).catch(() => {});
  }, [fetchDetail, fetchExtras, businessId]);

  // SSE-powered live status with fallback polling
  useSSEWithFallback(
    "truck_status_changed",
    (data) => { if (data.business_id === businessId) fetchLive(); },
    fetchLive,
    (data) => data.business_id === businessId,
  );

  const toggleFav = async () => {
    requireAuth("follow", async () => {
      try {
        await axios.post(`${API}/favorites/${businessId}`);
        setIsFav(prev => !prev);
        toast.success(isFav ? "Unfollowed" : "Following!");
      } catch { toast.error("Failed"); }
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: biz?.name, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href);
      toast.success("Link copied!");
    }
  };

  const saveVCard = () => {
    if (!biz?.vcard_data) { toast.error("No contact card available"); return; }
    const blob = new Blob([biz.vcard_data], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(biz?.name || "truck").replace(/\s+/g, "_")}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Contact saved!");
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!biz) {
    return (
      <div className="max-w-md mx-auto min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-slate-500 text-sm">Truck not found</p>
        <button onClick={() => navigate(-1)} className="text-[#F59E0B] font-bold text-sm">Go back</button>
      </div>
    );
  }

  const session = liveData?.live_session || biz?.live_session;
  const todayLoc = liveData?.today_location || biz?.today_location;
  const photo = biz.truck_photo || biz.logo_url || biz.image_url;
  const tags = biz.cuisine_tags || [];
  const schedule = biz.this_week_schedule || [];
  const payments = biz.payment_methods || [];
  const socials = biz.social_media || [];

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white pb-20" data-testid="truck-detail-page">
      <TipOfTheDay pageKey="food_truck_detail" />
      {/* ── Section 1: Hero ── */}
      <div className="relative" data-testid="truck-hero">
        <div className="w-full aspect-video overflow-hidden bg-gradient-to-br from-[#F59E0B] to-[#D97706]">
          {photo ? (
            <img src={photo} alt={biz.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-6xl">{tags.length > 0 ? getCuisineEmoji(tags[0]) : "🚚"}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
        </div>
        <button onClick={() => navigate(-1)} className="absolute top-3 left-3 w-11 h-11 rounded-full bg-black/50 flex items-center justify-center" data-testid="truck-back-btn">
          <ArrowLeft className="w-4 h-4 text-white" />
        </button>
        <span className="absolute top-3 left-14 px-2.5 py-1 rounded-full bg-[#1C1C1E] text-white text-[9px] font-black tracking-wider">TRUCK</span>

        {session?.status === "live" && (
          <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/70 backdrop-blur-sm">
            <div className="w-2.5 h-2.5 rounded-full bg-[#22C55E] animate-pulse" />
            <span className="text-xs font-bold text-white">LIVE NOW</span>
          </div>
        )}

        <div className="absolute bottom-3 left-4 right-4">
          <h1 className="text-xl font-black text-white leading-tight" data-testid="truck-detail-name">{biz.name}</h1>
          {biz.city && <p className="text-xs text-white/60 mt-0.5">{biz.city}{biz.state ? `, ${biz.state}` : ""}</p>}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 px-4 pt-3">
        <button onClick={toggleFav} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold transition ${isFav ? "bg-[#F59E0B] text-[#1C1C1E]" : "bg-slate-100 text-slate-700"}`} data-testid="truck-detail-follow">
          <Heart className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} />
          {isFav ? "Following" : "Follow"}
        </button>
        <button onClick={() => requireAuth("spot", () => setShowSpot(true))} className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-bold" data-testid="truck-detail-spot-btn">
          <Eye className="w-4 h-4" /> I see this truck
        </button>
        <button onClick={handleShare} className="flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold" data-testid="truck-detail-share">
          <Share2 className="w-4 h-4" />
        </button>
      </div>
      {/* Order button */}
      <div className="px-4 pt-2">
        <button
          onClick={() => navigate(`/truck/${businessId}/order`)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-sky-500 text-white text-sm font-bold hover:bg-sky-600 transition"
          data-testid="truck-order-btn"
        >
          <ShoppingCart className="w-4 h-4" /> Order from {biz?.truck_name || biz?.name || "this truck"}
        </button>
      </div>

      {/* Menu Preview — clickable, leads to order page */}
      {smartMenu && smartMenu.items && smartMenu.items.length > 0 && (
        <div className="px-4 pt-3">
          <button
            onClick={() => navigate(`/truck/${businessId}/order`)}
            className="w-full bg-gray-50 rounded-xl border border-gray-200 p-3.5 text-left hover:border-sky-300 hover:shadow-sm transition active:scale-[0.99]"
            data-testid="menu-preview-btn"
          >
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <Utensils className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-sm font-bold text-slate-800">Menu</span>
                <span className="bg-sky-100 text-sky-600 text-[10px] font-bold px-2 py-0.5 rounded-full">{smartMenu.items.filter(i => !i.sold_out).length} items</span>
              </div>
              <span className="text-sky-500 text-[10px] font-bold">View & Order →</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {smartMenu.items.filter(i => !i.sold_out).slice(0, 6).map((item) => (
                <span
                  key={item.item_id}
                  className="px-2 py-1 bg-white rounded-lg border border-gray-100 text-[11px] text-slate-700"
                >
                  {item.name}{item.price > 0 ? ` · $${item.price.toFixed(2)}` : ""}
                </span>
              ))}
              {smartMenu.items.filter(i => !i.sold_out).length > 6 && (
                <span className="px-2 py-1 bg-sky-50 rounded-lg text-[11px] text-sky-600 font-bold">
                  +{smartMenu.items.filter(i => !i.sold_out).length - 6} more
                </span>
              )}
            </div>
          </button>
        </div>
      )}

      <div className="px-4 mt-4 space-y-6">
        {/* ── Section 2: Live Status & Location ── */}
        <Section title="Location" testId="truck-location-section">
          {session?.status === "live" ? (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#22C55E] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-bold text-slate-800">{session.location_address || todayLoc?.address || "Live now"}</p>
                {todayLoc?.spot_count > 0 && <p className="text-[10px] text-slate-400 mt-0.5">Confirmed by {todayLoc.spot_count} {todayLoc.spot_count === 1 ? "person" : "people"}</p>}
              </div>
            </div>
          ) : session?.status === "scheduled" ? (
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-bold text-slate-800">Scheduled</p>
                <p className="text-[10px] text-slate-400">{session.location_address}</p>
              </div>
            </div>
          ) : todayLoc?.address ? (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#F59E0B] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-slate-700">{todayLoc.address}</p>
                {todayLoc.spotted_at && <p className="text-[10px] text-slate-400">Spotted {new Date(todayLoc.spotted_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
              <p className="text-sm text-slate-400">{biz.city ? `Serves ${biz.city}` : "Service area not set"}</p>
            </div>
          )}
        </Section>

        {/* Wait Time */}
        <WaitTimeDisplay
          waitMinutes={biz.wait_time_reported}
          waitCategory={biz.wait_time_category}
          reportedAt={biz.wait_time_reported_at}
          reporterCount={biz.wait_time_reported_by_count}
          onTap={() => requireAuth("wait_time", () => setShowWaitSheet(true))}
        />

        {/* Accountability Log */}
        <Section title="Spot History" testId="truck-spot-history">
          <AccountabilityLog businessId={businessId} />
        </Section>

        {/* ── Section 3: About ── */}
        <Section title="About" testId="truck-about-section">
          {biz.in_business_since && (
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-[#F59E0B]" />
              <p className="text-sm font-bold text-slate-800">In business since {biz.in_business_since}</p>
            </div>
          )}
          {biz.consistency_score > 0.70 && biz.consistency_total_sessions >= 5 && (
            <p className="text-[11px] text-[#F59E0B] font-medium mb-2" data-testid="consistency-public">
              {Math.round(biz.consistency_score * 100)}% on schedule
            </p>
          )}
          {/* Vendor achievement badges */}
          {allBadges.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1 mb-2 scrollbar-hide" data-testid="vendor-badges-row">
              {allBadges.map((badge, i) => (
                <span key={i} className="flex-shrink-0 px-2 py-1 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-[10px] font-medium border border-[#F59E0B]/20"
                  title={badge.label} data-testid={`badge-${badge.type}-${i}`}>
                  {badge.type === "volume" ? "🏆" : badge.type === "veteran" ? "⭐" : badge.type === "leaderboard" ? "🥇" : "🏅"} {badge.label}
                </span>
              ))}
            </div>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map(t => (
                <span key={t} className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium">
                  {getCuisineEmoji(t)} {t}
                </span>
              ))}
            </div>
          )}
          {payments.length > 0 && (
            <div className="flex gap-3 mb-2">
              {payments.map(p => {
                const info = PAYMENT_ICONS[p.toLowerCase()] || { icon: CreditCard, label: p };
                const Icon = info.icon;
                return (
                  <div key={p} className="flex items-center gap-1 text-[10px] text-slate-500">
                    <Icon className="w-3 h-3" /> {info.label}
                  </div>
                );
              })}
            </div>
          )}
          {/* Weather Card (Task 3) */}
          {weather && (
            <div className="bg-blue-50 rounded-xl p-4 mb-3" data-testid="truck-weather-card">
              <div className="text-center mb-2">
                <span className="text-3xl">{weather.condition_emoji}</span>
                <p className="text-2xl font-black text-slate-800">{weather.temperature_f}°F</p>
                <p className="text-xs text-slate-400">Feels like {weather.feels_like_f}°F</p>
              </div>
              <div className="space-y-1 text-xs text-slate-600">
                <p>Rain today: {weather.rain_probability_percent}%</p>
                <p>Wind: {weather.wind_mph} mph</p>
                {weather.best_window && (
                  <p>Best time to visit: {weather.best_window.start_hour > 12 ? `${weather.best_window.start_hour - 12}PM` : weather.best_window.start_hour === 12 ? "12PM" : `${weather.best_window.start_hour}AM`} to {weather.best_window.end_hour > 12 ? `${weather.best_window.end_hour - 12}PM` : weather.best_window.end_hour === 12 ? "12PM" : `${weather.best_window.end_hour}AM`} — {weather.best_window.reason}</p>
                )}
              </div>
            </div>
          )}
          {weatherPolicy && (
            <p className="text-[11px] text-slate-500 mb-1" data-testid="truck-weather-policy">{weatherPolicy}</p>
          )}
          {biz.weather_policy && <p className="text-[11px] text-slate-500 mb-1">{biz.weather_policy}</p>}
          {biz.typical_wait_minutes && (
            <p className="text-[11px] text-slate-500 mb-1">
              <Clock className="w-3 h-3 inline mr-1" />Typical wait: {biz.typical_wait_minutes} minutes
            </p>
          )}
          {biz.private_events_available && (
            <p className="text-[11px] text-[#F59E0B] font-medium">Available for private events — contact to book</p>
          )}
          {communityFavs.length > 0 && (
            <div className="mt-3" data-testid="community-favorites">
              <p className="text-[10px] font-bold text-slate-500 uppercase mb-1.5">Community Favorites</p>
              <div className="flex flex-wrap gap-1.5">
                {communityFavs.map(f => (
                  <span key={f.item} className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium capitalize">
                    {f.item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* ── Section 4: Birthday Special ── */}
        {biz.offer_title && (
          <Section title="Birthday Special" testId="truck-birthday-section">
            <div className="bg-amber-50 rounded-xl p-3">
              <p className="text-sm font-bold text-slate-800">🎂 {biz.offer_title}</p>
              {biz.offer_summary && biz.offer_summary !== biz.offer_title && (
                <p className="text-[11px] text-slate-600 mt-1">{biz.offer_summary}</p>
              )}
              {biz.offer_window_days && (
                <p className="text-[10px] text-slate-400 mt-1">Valid within {biz.offer_window_days} days of your birthday</p>
              )}
              <p className="text-[10px] text-slate-400 mt-0.5">Show your BirthdayDeaLight profile to claim</p>
              <button
                onClick={() => navigate(`/offer/${businessId}`)}
                className="mt-2 w-full py-2 rounded-xl bg-[#F59E0B] text-[#1C1C1E] text-xs font-bold hover:bg-[#D97706] transition"
                data-testid="truck-claim-btn"
              >
                View Full Offer Details
              </button>
            </div>
          </Section>
        )}

        {/* ── Section 5: This Week Schedule ── */}
        <Section title="This Week" testId="truck-schedule-section">
          {schedule.length > 0 ? (
            <div className="space-y-2">
              {schedule.map((day, i) => {
                // Check if this day is marked as unavailable
                const dateStr = day.date; // YYYY-MM-DD if available
                const isUnavail = dateStr && unavailableDates[dateStr];
                const unavailReason = isUnavail && typeof unavailableDates[dateStr] === "string" ? unavailableDates[dateStr] : null;

                return (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-xs font-medium text-slate-600 w-14">{day.day || `Day ${i + 1}`}</span>
                  {isUnavail ? (
                    <div className="flex-1 ml-2">
                      <p className="text-xs text-red-500/80 font-medium" data-testid={`schedule-unavailable-${i}`}>NOT OPERATING</p>
                      {unavailReason && <p className="text-[10px] text-red-400/60">{unavailReason}</p>}
                    </div>
                  ) : day.location ? (
                    <div className="flex-1 ml-2">
                      <p className="text-xs text-slate-700">{day.location}</p>
                      {day.start && day.end && <p className="text-[10px] text-slate-400">{day.start} — {day.end}</p>}
                    </div>
                  ) : (
                    <span className="text-[10px] text-slate-300 italic">Not scheduled</span>
                  )}
                </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-slate-400">No schedule set this week. Follow to get notified when they go live.</p>
          )}
        </Section>

        {/* ── Section 6: Loyalty Punch Card ── */}
        <Section title="Loyalty Card" testId="truck-punch-section">
          <PunchCard businessId={businessId} businessName={biz.name} />
        </Section>

        {/* ── Section 6b: Group Order ── */}
        {session?.status === "live" && (
          <Section title="Group Order" testId="truck-group-order-section">
            <GroupOrder businessId={businessId} businessName={biz.name} />
          </Section>
        )}

        {/* ── Section 7: Social Media ── */}
        {socials.length > 0 && (
          <Section title="Social Media" testId="truck-social-section">
            <SocialLinksSection socialMedia={socials} businessId={businessId} businessName={biz.name} />
          </Section>
        )}

        {/* ── External Links (Open App / Visit Website) ── */}
        {(biz.app_store_url || biz.website_url) && (
          <div className="px-4 mb-3" data-testid="truck-external-links">
            <ExternalLinkButtons app_store_url={biz.app_store_url} website_url={biz.website_url} business_name={biz.name} />
          </div>
        )}

        {/* ── Section 8: Digital Business Card ── */}
        {biz.vcard_data && (
          <Section title="Save Contact" testId="truck-vcard-section">
            <button
              onClick={saveVCard}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition"
              data-testid="truck-save-contact-btn"
            >
              <Download className="w-4 h-4" /> Save to Contacts
            </button>
          </Section>
        )}

        {/* ── Section 9: Cross Promotion ── */}
        {biz.has_parent_location && biz.parent_business_id && (
          <Section title="Also Visit" testId="truck-cross-promo-section">
            <p className="text-xs text-slate-600 mb-2">This truck also has a brick & mortar location</p>
            <Link
              to={`/offer/${biz.parent_business_id}`}
              className="flex items-center gap-2 text-[#F59E0B] text-xs font-bold"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View location
            </Link>
          </Section>
        )}

        {/* ── Section 10: Report ── */}
        <div className="text-center pt-4 pb-8">
          <button
            onClick={() => navigate(`/offer/${businessId}/verifications`)}
            className="text-[10px] text-slate-300 hover:text-slate-500 transition"
            data-testid="truck-report-link"
          >
            Something wrong with this listing? Tap to report
          </button>
        </div>
      </div>

      {/* Spot Submission Sheet */}
      {showSpot && (
        <SpotSubmissionSheet
          businessId={businessId}
          businessName={biz?.name}
          onClose={() => setShowSpot(false)}
          onSuccess={() => { fetchLive(); fetchExtras(); }}
        />
      )}

      {/* Wait Time Sheet */}
      {showWaitSheet && (
        <WaitTimeSheet
          businessId={businessId}
          businessName={biz?.name}
          onClose={() => setShowWaitSheet(false)}
          onSuccess={() => fetchDetail()}
        />
      )}
      <AuthGateSheet {...gateProps} />
    </div>
  );
}

function Section({ title, testId, children }) {
  return (
    <div data-testid={testId}>
      <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{title}</h2>
      {children}
    </div>
  );
}
